// The rule that replaces the debounce.
//
// The search boxes used to wait 500 ms after the last keystroke before they sent anything
// (`Header.tsx`, `stock-search-bar/Searchbar.tsx`), then 120 ms once omnisearch was on.
// That existed because the backend could not take the load: a `LIKE '%term%'` scan
// measured at 380-480 ms uncached. The omnisearch endpoint answers in ~137 us of CPU
// behind a 21-24 ms round trip, so any wait is now the largest single component of
// perceived latency.
//
// It is replaced by three rules, none of which involve waiting:
//
//  1. **Fire on the keystroke.** No timer.
//  2. **Coalesce.** One request in flight at a time; while one is out, the newest query
//     is held and sent the moment it returns. Typing "tesla" sends two or three requests,
//     not five, without ever delaying the first. This also bounds the legacy fallback:
//     the whole lookup (omnisearch, then `/API/v2/find/` when that has nothing) is one
//     unit, so a slow legacy answer holds the pipe rather than piling requests up.
//  3. **Drop stale answers.** Only the answer for the text in the box now is delivered,
//     so a slow response for "tes" can never overwrite a fast one for "tesla".

import { OmniResult } from "./types";

/** The longest one lookup may hold the pipe: omnisearch's 2.5 s plus a slow legacy answer. */
const LOOKUP_TIMEOUT_MS = 6000;

/**
 * One search box's request pipe. Create one per mounted box; call `query` on every
 * keystroke, `cancel` when the box is cleared and `dispose` on unmount.
 */
export class LatestSearch<T> {
    private latest = "";
    private inFlight = false;
    private pending: string | null = null;
    private disposed = false;

    constructor(
        private readonly run: (text: string) => Promise<T>,
        private readonly deliver: (text: string, result: T) => void,
        /** The lookup for the text in the box failed or timed out: stop showing it as loading. */
        private readonly fail?: (text: string) => void
    ) {}

    query(text: string): void {
        if (this.disposed) return;
        this.latest = text;
        if (this.inFlight) {
            // Newest wins; an intermediate keystroke that was never sent is simply skipped.
            this.pending = text;
            return;
        }
        void this.send(text);
    }

    /** Forget the current text: nothing in flight or pending will be delivered. */
    cancel(): void {
        this.latest = "";
        this.pending = null;
    }

    private async send(text: string): Promise<void> {
        this.inFlight = true;
        let timer: ReturnType<typeof setTimeout> | undefined;
        try {
            // One lookup holds the pipe, so a hung one (the legacy endpoint has no timeout of
            // its own) must not park every later keystroke: give up on it and move on.
            const result = await Promise.race([
                this.run(text),
                new Promise<never>((_, reject) => {
                    timer = setTimeout(() => reject(new Error("search lookup timed out")), LOOKUP_TIMEOUT_MS);
                }),
            ]);
            // A slow answer for an earlier keystroke must never replace a newer one.
            if (!this.disposed && text === this.latest) this.deliver(text, result);
        } catch {
            // Deliberately quiet: a search box that reports an error is worse than one
            // that shows fewer rows. `run` should not throw; a timeout lands here too.
            if (!this.disposed && text === this.latest && this.pending === null) this.fail?.(text);
        } finally {
            clearTimeout(timer);
            this.inFlight = false;
            const next = this.pending;
            this.pending = null;
            if (next !== null && !this.disposed) void this.send(next);
        }
    }

    dispose(): void {
        this.disposed = true;
        this.pending = null;
    }
}

/**
 * Merge the local and server answers.
 *
 * Server rows are authoritative - they carry the ids, the collapse count and the whole
 * corpus - so a server row replaces a local row for the same route. Local rows the
 * server did not return stay on, ranked below, which is what stops the dropdown flashing
 * empty between the two tiers.
 */
export function mergeResults(local: OmniResult[], server: OmniResult[], limit = 10): OmniResult[] {
    const seen = new Set(server.map((r) => r.route));
    return [...server, ...local.filter((r) => !seen.has(r.route))].slice(0, limit);
}
